import type { ColumnType } from 'antd/es/table';
import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';

import DateTime from '~/components/Columns/DateTime';

import type { Model } from '~/types/models';
import type { StringKeys } from '~/types/utils';

export function getColumnFromKey<R, T = any>(
  columns: Array<ColumnDefinition<R, T>>,
  key: string,
): ColumnDefinition<R, T> | undefined {
  return columns.find((c) => c.key === key);
}

export function isColumnType<T>(column: any): column is ColumnType<T> {
  return !!column && typeof column === 'object' && typeof column.dataIndex === 'string';
}

export function isColumnDefinition<R>(column: any): column is ColumnDefinition<R> {
  return (
    !!column &&
    typeof column === 'object' &&
    typeof column.key === 'string' &&
    typeof column.sortable === 'boolean' &&
    typeof column.title === 'string' &&
    (typeof column.type === 'string' || Array.isArray(column.type)) &&
    (typeof column.render === 'function' || column.render === undefined)
  );
}

export function isCollectionColumnDefinition<R, T>(column: any): column is CollectionColumnDefinition<R, T> {
  return (isColumnDefinition(column) as any) && column.type === 'collection';
}

export function isEnumColumnDefinition<R>(column: any): column is EnumColumnDefinition<R> {
  return (isColumnDefinition(column) as any) && Array.isArray(column.options);
}

export function isItemColumnDefinition<R, T>(column: any): column is ItemColumnDefinition<R, T> {
  return (isColumnDefinition(column) as any) && column.type === 'item';
}

export const DateFilterPresets: Record<string, Record<string, string>> = {
  Past: {
    'P2D:PT0S': 'Dans les deux derniers jours',
    'P7D:PT0S': 'Dans la dernière semaine',
    'P1M:PT0S': 'Dans le dernier mois',
    'P3M:PT0S': 'Dans les derniers trois mois',
    custom: 'Entre deux dates',
  },
  Future: {
    'PT0S:P2D': "D'ici deux jours",
    'PT0S:P7D': "D'ici une semaine",
    'PT0S:P1M': "D'ici un moins",
    'PT0S:P3M': "D'ici trois mois",
    custom: 'Entre deux dates',
  },
};

interface BaseColumnDefinition<R, T = any> {
  editable?: boolean;
  filterable?: boolean;
  hint?: string;
  importable?: boolean;
  key: StringKeys<R>;
  nullable?: string;
  notNullable?: string;
  only?: ViewSlug[];
  render?: (value: T[] | T | undefined | null, record?: R, index?: number) => React.ReactNode;
  readOnly?: boolean;
  sortable?: boolean;
  title: string;
  toLabel?: (value: string | string[]) => React.ReactElement;
  width?: string;
  with?: string;
}

export interface BooleanColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'boolean';
  labelFalse?: string;
  labelTrue?: string;
}

export interface CollectionColumnDefinition<R, T> extends Omit<BaseColumnDefinition<R, T>, 'render'> {
  type: 'collection';
  lazy?: boolean;
  mapOptions?: (arg: T[]) => DefaultOptionType[];
  prefetchOptions?: () => Promise<T[]>;
  render: (value: T[] | null | undefined, record?: R, index?: number) => React.ReactNode;
}

export interface CountColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'count';
  render?: (value: number, record?: R, index?: number) => React.ReactNode;
}

export interface DateColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'date';
  presets: Record<string, string>;
}

export interface DateTimeColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'datetime';
  presets: Record<string, string>;
}

export interface EnumColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'enum';
  options: DefaultOptionType[];
}

export interface HiddenColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'hidden';
}

export interface ItemColumnDefinition<R, T> extends Omit<BaseColumnDefinition<R, T>, 'render'> {
  type: 'item';
  lazy?: boolean;
  mapOptions?: (arg: T[]) => DefaultOptionType[];
  prefetchOptions?: () => Promise<T[]>;
  render: (value: T | null | undefined, record?: R, index?: number) => React.ReactNode;
}

export interface NumberColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'number';
}

export interface StringColumnDefinition<R> extends BaseColumnDefinition<R> {
  mask?: string;
  type: 'string';
}

export interface SystemColumnDefinition<R> extends BaseColumnDefinition<R> {
  type: 'system';
}

export type ViewSlug = 'single' | 'index';

export type ColumnDefinition<R, T = any> =
  | BooleanColumnDefinition<R>
  | CollectionColumnDefinition<R, T>
  | CountColumnDefinition<R>
  | DateColumnDefinition<R>
  | DateTimeColumnDefinition<R>
  | EnumColumnDefinition<R>
  | HiddenColumnDefinition<R>
  | ItemColumnDefinition<R, T>
  | NumberColumnDefinition<R>
  | StringColumnDefinition<R>
  | SystemColumnDefinition<R>;

export const SystemColumns: Array<ColumnDefinition<Model>> = [
  {
    key: 'id',
    readOnly: true,
    sortable: false,
    title: 'Identifiant unique (GUID)',
    type: 'system',
    width: '62px',
  },
  {
    key: 'sequence',
    readOnly: true,
    sortable: true,
    title: 'ID',
    type: 'system',
    render: (_, record) => record?.humanId,
  },
];

export const ModelColumns: Array<ColumnDefinition<Model>> = [
  ...SystemColumns,
  {
    importable: false,
    key: 'created',
    presets: DateFilterPresets.Past,
    title: 'Création',
    type: 'datetime',
    readOnly: true,
    sortable: true,
    render: (v) => <DateTime value={v} />,
  },
  {
    importable: false,
    key: 'lastUpdated',
    presets: DateFilterPresets.Past,
    title: 'Mise à jour',
    type: 'datetime',
    sortable: true,
    readOnly: true,
    render: (v) => <DateTime value={v} />,
  },
];
